<template>
  <component
    :is="cardAsLink && props.detailLink ? 'a' : 'div'"
    :href="cardAsLink ? url : undefined"
    :target="cardAsLink ? urlTarget : undefined"
    :rel="cardAsLink && urlTarget === '_blank' ? 'noopener noreferrer' : ''"
    class="AiCarouselOverflowItem"
    @click="cardAsLink ? handleClick : undefined">
    <nuxt-link
      v-if="!cardAsLink && imageClickable === 'link'"
      :class="{
        'AiCarouselOverflowItem-background--clickable': props.detailLink,
      }"
      :to="url"
      class="AiCarouselOverflowItem-background">
      <ai-image
        v-if="picture?.src"
        :alt="picture?.alt as string"
        :lg="20"
        :loading="lazyLoadPicture ? 'lazy' : 'eager'"
        :md="35"
        :sm="65"
        :src="picture?.src"
        :src-set="picture?.srcSet as string"
        :xl="25"
        class="AiCarouselOverflowItem-image"
        decorative />
      <div class="AiCarouselOverflowItem-gradient" />
    </nuxt-link>

    <component
      :is="imageClickable === 'button' ? 'button' : 'div'"
      v-else
      :class="{
        'AiCarouselOverflowItem-background--clickable': props.detailLink,
      }"
      class="AiCarouselOverflowItem-background"
      @click="imageClickable === 'button' ? handleClick : undefined">
      <ai-image
        v-if="picture?.src"
        :alt="picture?.alt as string"
        :loading="lazyLoadPicture ? 'lazy' : 'eager'"
        :src="picture?.src"
        :src-set="picture?.srcSet as string"
        class="AiCarouselOverflowItem-image"
        decorative />
      <div class="AiCarouselOverflowItem-gradient" />
    </component>

    <div class="AiCarouselOverflowItem-content">
      <slot name="top" />
      <ai-typo
        :class="`AiCarouselOverflowItem-title--${margin}`"
        :variant="titleVariantTypo"
        as="p"
        class="AiCarouselOverflowItem-title">
        {{ title }}
      </ai-typo>

      <ai-typo
        as="p"
        class="AiCarouselOverflowItem-description"
        variant="paragraph-04-regular">
        {{ description }}
      </ai-typo>

      <slot name="button">
        <div class="AiCarouselOverflowItem-buttonWrapper">
          <ai-button
            v-if="detailLink"
            :href="url"
            :icon-right="'button-arrow-right' as IconName"
            :label="ctaLabel"
            :rel="urlTarget === '_blank' ? 'noopener noreferrer' : undefined"
            :target="urlTarget"
            class="AiCarouselOverflowItem-button"
            light
            variant="tertiary"
            @click.stop="noop" />
        </div>
      </slot>
    </div>
  </component>
</template>

<script lang="ts" setup>
import noop from 'lodash/noop';

import type AiButton from '../../atoms/AiButton/AiButton.vue';
import type { IconName } from '../../atoms/AiIcon/types';
import type AiImage from '../../atoms/AiImage/AiImage.vue';
import type AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import type { AiTypoVariant } from '../../atoms/AiTypo/types';

import { useGlobalConfigurationAsyncData } from '~~/domains/graphql';
import type { AiPicture } from '~~/domains/graphql';
import { TAG_MAPPING_BY_EXPERIENCE_ID } from '~~/domains/home/constants';

interface Props {
  title: string;
  description: string;
  ctaLabel?: string;
  picture?: AiPicture;
  detailLink?: string;
  cardAsLink?: boolean;
  titleVariantTypo?: AiTypoVariant;
  margin?: 'margin-01' | 'inner-01';
  lazyLoadPicture?: boolean;
  imageClickable?: 'button' | 'link';
}

const props = withDefaults(defineProps<Props>(), {
  ctaLabel: '',
  detailLink: '',
  margin: 'margin-01',
  titleVariantTypo: 'heading-04',
  imageClickable: undefined,
  picture: undefined,
});

const config = useRuntimeConfig();
const { data: globalConfigurationData } = useGlobalConfigurationAsyncData();
const { getRedirectToSearchUrl } = useExternalNavigation();

const countryCodeFromName = (name: string) => {
  if (/^[a-z]{2}$/i.test(name)) return name.toUpperCase();

  const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
  for (let first = 65; first <= 90; first++) {
    for (let second = 65; second <= 90; second++) {
      const code = String.fromCharCode(first, second);
      if (countryNames.of(code)?.toLowerCase() === name.toLowerCase()) {
        return code;
      }
    }
  }

  return undefined;
};

const searchParamsFromUrl = (searchUrl: URL) => {
  const destinationQuery = searchUrl.searchParams.get('destination');
  let destination: string | undefined;

  if (destinationQuery) {
    try {
      const parsed = JSON.parse(destinationQuery);
      if (parsed.type !== 'COUNTRY' || typeof parsed.text !== 'string') {
        return undefined;
      }
      destination = countryCodeFromName(parsed.text);
      if (!destination) return undefined;
    } catch {
      return undefined;
    }
  }

  const experienceCategoryId = searchUrl.searchParams.get(
    'experienceCategoryId',
  );
  const tag = experienceCategoryId
    ? (TAG_MAPPING_BY_EXPERIENCE_ID as Record<string, string>)[
        experienceCategoryId
      ]
    : undefined;
  if (experienceCategoryId && !tag) return undefined;

  return { destination, tags: tag ? ([tag] as [string]) : undefined };
};

const url = computed(() => {
  const detailLink = props.detailLink;
  const bookingConfig =
    globalConfigurationData.value?.globalConfiguration?.booking;
  const externalSearchLink = bookingConfig?.externalSearchPageUrl;

  if (
    !detailLink ||
    !bookingConfig?.useExternalSearchPage ||
    !externalSearchLink
  ) {
    return detailLink || undefined;
  }

  let searchUrl: URL;
  let externalSearchUrl: URL;
  try {
    searchUrl = new URL(detailLink, 'https://local.invalid');
    externalSearchUrl = new URL(externalSearchLink);
  } catch {
    return detailLink;
  }

  if (
    /^(?:https?:)?\/\//i.test(detailLink) &&
    searchUrl.host === externalSearchUrl.host
  ) {
    return detailLink;
  }

  if (!/^\/(?:[a-z]{2}\/)?search\/?$/i.test(searchUrl.pathname)) {
    return detailLink;
  }

  const searchParams = searchParamsFromUrl(searchUrl);
  return searchParams
    ? getRedirectToSearchUrl(externalSearchLink, searchParams)
    : detailLink;
});
const urlTarget = computed(() => {
  if (!url.value) return undefined;

  if (url.value.startsWith('/')) return '_self';

  try {
    const location = new URL(url.value);
    const baseLocation = new URL(config.public.baseUrl);

    return location.host !== baseLocation.host ? '_blank' : '_self';
  } catch (e) {
    /* eslint-disable-next-line no-console */
    console.warn(e);
  }

  return undefined;
});

const handleClick = () => {
  if (!url.value) return;

  const targetUrl = new URL(url.value, window.location.href);

  if (targetUrl.host !== window.location.host) {
    window.open(url.value, urlTarget.value);
  } else {
    window.location.href = url.value;
  }
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/mixins';

.AiCarouselOverflowItem {
  text-decoration: none;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  @include mixins.rem-fallback(min-height, 480);

  @media (mq.$from-medium) {
    @include mixins.rem-fallback(min-height, 552);
  }
}

.AiCarouselOverflowItem-background {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  &--clickable:hover {
    .AiCarouselOverflowItem-image {
      transform: scale(1.1);
    }
  }
}

.AiCarouselOverflowItem-gradient {
  position: absolute;
  inset: 0px;
  background: constants.$linear-gradient-smoke;
  z-index: 1;
}

.AiCarouselOverflowItem-image {
  position: absolute;
  inset: 0;
  overflow: hidden;
  transition: all 300ms;
  @include mixins.rem-fallback(height, 480);

  z-index: 0;

  @media (mq.$from-medium) {
    @include mixins.rem-fallback(height, 552);
  }
}

.AiCarouselOverflowItem-content {
  z-index: 5;
  padding: constants.$padding-04 constants.$padding-01;
  text-align: center;
  min-height: calc(480px / 2);
  text-overflow: ellipsis;

  @media (mq.$from-large) {
    min-height: calc(552px / 2);
    padding: constants.$padding-04 40px;
    text-overflow: ellipsis;
  }
}

.AiCarouselOverflowItem-title {
  color: colors.$white;

  &--margin-01 {
    margin-bottom: constants.$margin-01;
  }

  &--inner-01 {
    margin-bottom: constants.$inner-01;
  }
}

.AiCarouselOverflowItem-description {
  color: colors.$white;
}

.AiCarouselOverflowItem-buttonWrapper {
  text-align: center;
}

.AiCarouselOverflowItem-button {
  display: inline-block;
  margin-top: constants.$margin-01;
}
</style>
